import {AxiosError, UseFetchOptions, UseFetchReturn} from './types';

import {B2BUserScreens} from '@buncha/B2B/navigation/types';
import {MainScreens} from '@buncha/navigation/types';
import {apiService} from '../axios/service';
import {navigation} from '../navigation';
import {useState} from 'react';

/**
 * Generic data-fetching hook using apiService
 */
const useFetchData = <
  TResponse,
  TRequest extends object = {},
>(): UseFetchReturn<TResponse> => {
  const [data, setData] = useState<TResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<AxiosError | null>(null);

  const fetchData = async (
    endpoint: string,
    options: UseFetchOptions<TRequest> = {},
  ) => {
    const {method = 'get', body} = options;
    setLoading(true);
    setError(null);

    try {
      let response: TResponse;

      switch (method.toLowerCase()) {
        case 'get':
          response = await apiService.get<TResponse>(endpoint, body ?? {});
          break;
        case 'post':
          response = await apiService.post<TResponse>(endpoint, body);
          break;
        case 'put':
          response = await apiService.put<TResponse>(endpoint, body);
          break;
        case 'delete':
          response = await apiService.delete<TResponse>(endpoint, body ?? {});
          break;
        default:
          throw new Error(`Unsupported HTTP method: ${method}`);
      }

      setData(response);
      return response;
    } catch (err: any) {
      const errorAxios: AxiosError = err;
      const unAuthStatus = [401, 403];
      if (unAuthStatus.includes(errorAxios?.response?.status))
        navigation.navigate(MainScreens.B2B, {
          screen: B2BUserScreens.UserLogin,
        });

      setError(err);
    } finally {
      setLoading(false);
    }
  };

  return [fetchData, data, loading, error];
};

export const useFetchGetData = <
  TResponse,
  TRequest extends object = {},
>(): UseFetchReturn<TResponse> => {
  const [func, data, loading, error] = useFetchData<TResponse>();

  const fetchData = async (
    endpoint: string,
    options: UseFetchOptions<TRequest> = {},
  ) => {
    return await func(endpoint, {...options, method: 'get'});
  };

  return [fetchData, data, loading, error];
};

export const useFetchPostData = <
  TResponse,
  TRequest extends object = {},
>(): UseFetchReturn<TResponse> => {
  const [func, data, loading, error] = useFetchData<TResponse>();

  const fetchData = async (
    endpoint: string,
    options: UseFetchOptions<TRequest> = {},
  ) => {
    return await func(endpoint, {...options, method: 'post'});
  };

  return [fetchData, data, loading, error];
};

export const useFetchPutData = <
  TResponse,
  TRequest extends object = {},
>(): UseFetchReturn<TResponse> => {
  const [func, data, loading, error] = useFetchData<TResponse>();

  const fetchData = async (
    endpoint: string,
    options: UseFetchOptions<TRequest> = {},
  ) => {
    return await func(endpoint, {...options, method: 'put'});
  };

  return [fetchData, data, loading, error];
};

export const useFetchDeleteData = <
  TResponse,
  TRequest extends object = {},
>(): UseFetchReturn<TResponse> => {
  const [func, data, loading, error] = useFetchData<TResponse>();

  const fetchData = async (
    endpoint: string,
    options: UseFetchOptions<TRequest> = {},
  ) => {
    return await func(endpoint, {...options, method: 'delete'});
  };

  return [fetchData, data, loading, error];
};
